import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { drawMilestone } from "@/lib/milestoneCanvas";
import { Button, Spinner } from "@/components/ui-kit";
import { APP } from "@/config";
import { ArrowRight, Sparkles } from "lucide-react";

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
}

export default function PublicMilestone() {
  const { token } = useParams();
  const nav = useNavigate();
  const canvasRef = useRef(null);
  const [card, setCard] = useState(null);
  const [err, setErr] = useState(false);
  useNoIndex();

  useEffect(() => {
    api.get(`/public/m/${token}`).then(({ data }) => {
      setCard(data);
      track("milestone_page_viewed", { token });
    }).catch(() => setErr(true));
  }, [token]);

  useEffect(() => {
    if (card && canvasRef.current) drawMilestone(canvasRef.current, card, "square");
  }, [card]);

  const toDiagnostic = () => {
    track("milestone_cta_click", { token });
    nav("/diagnostic");
  };

  if (err) return <div className="band-3-5 min-h-screen bg-[var(--ns-bg)] grid place-items-center text-[var(--ns-muted)]">This milestone link is no longer available.</div>;
  if (!card) return <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]"><Spinner label="Loading" /></div>;

  return (
    <div className="band-3-5 min-h-screen bg-[var(--ns-bg)]">
      <header className="max-w-2xl mx-auto px-5 py-5 flex items-center gap-2">
        <div className="w-9 h-9 rounded-xl bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold">N</div>
        <span className="font-head text-lg font-bold text-[var(--ns-text)]">{APP.name}</span>
      </header>
      <main className="max-w-md mx-auto px-5 pb-16">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="rounded-[var(--ns-radius)] overflow-hidden border border-[var(--ns-border)] bg-[var(--ns-surface-subtle)] p-3 grid place-items-center" data-testid="public-milestone-card">
            <canvas ref={canvasRef} className="w-full h-auto rounded-lg shadow-sm" />
          </div>

          <div className="mt-6 rounded-[var(--ns-radius)] bg-[var(--ns-surface)] border border-[var(--ns-border)] p-6 text-center">
            <div className="w-12 h-12 mx-auto rounded-xl grid place-items-center mb-3" style={{ background: "var(--ns-primary-light)", color: "var(--ns-primary)" }}>
              <Sparkles size={24} />
            </div>
            <h1 className="font-head text-xl font-bold">Where does your child stand?</h1>
            <p className="text-sm text-[var(--ns-muted)] mt-1 max-w-sm mx-auto">
              NumberSense finds the real math gaps in about 5 minutes — free, no account needed.
            </p>
            <Button className="mt-4 mx-auto px-7" data-testid="milestone-diagnostic-cta" onClick={toDiagnostic}>
              Try the free check <ArrowRight size={18} />
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
